import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Podcast, Mic2, Users, FileText, Link2, Sparkles, Loader2, Check,
  ChevronLeft, ChevronRight, Zap, Download, Volume2, Music2, Trash2,
  Star, ExternalLink, Play, Pause, Upload, RefreshCw, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { appConfirm } from "@/components/AppDialogHost";

// ── Design tokens (aligned with Quick Promo mockup) ─────────────────
const T = {
  bg:       "#0f1117",
  card:     "#1a1e2a",
  elevated: "#161923",
  border:   "#232632",
  primary:  "#4a9eff",
  purple:   "#7c5cff",
  success:  "#3ddc97",
  warning:  "#ffb84a",
  danger:   "#f87171",
  text:     "#e7ebf6",
  muted:    "#7b8397",
  faint:    "#4a5060",
};

const FORMATS = [
  { id: "solo",  icon: Mic2,  title: "Solo host",  subtitle: "One AI voice narrates the whole episode. Fast, clean, focused." },
  { id: "duet",  icon: Users, title: "Two-speaker conversation", subtitle: "Two AI hosts chat back and forth. Great for deeper topics." },
];

const SOURCE_MODES = [
  { id: "topic",  icon: Sparkles,  label: "Topic prompt",   hint: "Tell the AI what to talk about" },
  { id: "url",    icon: Link2,     label: "Article URL",    hint: "Paste a URL — AI summarizes into podcast form" },
  { id: "text",   icon: FileText,  label: "Upload script",  hint: "Bring your own script — we just narrate it" },
];

const MUSIC_MODES = [
  { id: "full",     title: "Full production", subtitle: "Intro sting + bed under narration + outro sting" },
  { id: "bookends", title: "Bookends only",   subtitle: "Intro + outro stings, no bed under speech (clearer voice)" },
  { id: "dry",      title: "Dry narration",   subtitle: "No music — just voice. Add music later in Production Suite." },
];

export default function PodcastCreator() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Wizard state
  const [step, setStep]           = useState(1);
  const [format, setFormat]       = useState("solo");
  const [sourceMode, setSourceMode] = useState("topic");

  // Step 2 — Source inputs
  const [topic, setTopic]         = useState("");
  const [urlInput, setUrlInput]   = useState("");
  const [textInput, setTextInput] = useState("");
  const [targetMinutes, setTargetMinutes] = useState(5);
  const [tone, setTone]           = useState("informative, engaging");
  const [hostAName, setHostAName] = useState("Host");
  const [hostBName, setHostBName] = useState("Co-host");
  const [generating, setGenerating] = useState(false);

  // Script state (post-generation, before render)
  const [script, setScript]       = useState(null);   // {title, description, segments[]}

  // Step 3 — Voices
  const [voices, setVoices]         = useState([]);
  const [voiceAId, setVoiceAId]     = useState("fable");
  const [voiceBId, setVoiceBId]     = useState("nova");

  // Step 4 — Music
  const [musicMode, setMusicMode] = useState("bookends");
  const [beds, setBeds]           = useState([]);
  const [introBedId, setIntroBedId] = useState(null);
  const [outroBedId, setOutroBedId] = useState(null);
  const [underBedId, setUnderBedId] = useState(null);
  const [underBedGain, setUnderBedGain] = useState(0.2);

  // Step 5 — Render + result
  const [publishToVault, setPublishToVault] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [renderJob, setRenderJob] = useState(null);
  const [renderedEp, setRenderedEp] = useState(null);
  const [audioUrl, setAudioUrl]   = useState(null);
  const [showNotes, setShowNotes] = useState("");

  // ── Preview state (voices + beds share one "now playing" slot) ──
  const [previewingKey, setPreviewingKey] = useState(null);
  const previewAudioRef = useRef(null);

  // Custom bed upload
  const [uploadingBed, setUploadingBed] = useState(false);
  const bedFileInputRef = useRef(null);

  // ── Draft state (auto-save + resume) ─────────────────────────────
  // draftId is the server-assigned ID; once we save the first draft
  // (or resume an existing one), we keep upserting into the same row.
  const [draftId, setDraftId] = useState(null);
  const [drafts, setDrafts]   = useState([]);        // latest 5 on landing
  const [draftsLoading, setDraftsLoading] = useState(true);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftBusy, setDraftBusy] = useState(false);  // guards concurrent PUTs
  const saveTimerRef = useRef(null);

  // ── Load voices + beds on mount ─────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [vr, br] = await Promise.all([
          api.get("/podcast/creator/voices"),
          api.get("/promo/beds?limit=50"),
        ]);
        setVoices(vr.data.voices || []);
        setVoiceAId(vr.data.default_voice_id || "fable");
        // Pick a different default for host_b
        const secondary = (vr.data.voices || []).find((v) =>
          v.id !== (vr.data.default_voice_id || "fable") && !v.needs_key && v.long_form_recommended);
        if (secondary) setVoiceBId(secondary.id);
        setBeds(br.data.items || []);
      } catch {
        toast.error("Could not load podcast tools");
      }
    })();
  }, []);

  // ── Preview (single-slot player shared across voices + beds) ────
  const stopPreview = useCallback(() => {
    if (previewAudioRef.current) {
      try {
        // Detach handlers BEFORE clearing src — otherwise setting src=""
        // fires onerror on the old element and shows a false error toast.
        previewAudioRef.current.onerror = null;
        previewAudioRef.current.onended = null;
        previewAudioRef.current.pause();
        previewAudioRef.current.src = "";
      } catch { /* no-op */ }
      previewAudioRef.current = null;
    }
    setPreviewingKey(null);
  }, []);

  useEffect(() => () => stopPreview(), [stopPreview]);

  const playPreview = useCallback(async (key, endpoint) => {
    if (previewingKey === key) {
      stopPreview();
      return;
    }
    stopPreview();
    setPreviewingKey(key);
    try {
      const r = await api.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const audio = new Audio(url);
      audio.onended = () => {
        URL.revokeObjectURL(url);
        setPreviewingKey((k) => (k === key ? null : k));
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        setPreviewingKey((k) => (k === key ? null : k));
        toast.error("Preview playback failed");
      };
      previewAudioRef.current = audio;
      await audio.play();
    } catch (e) {
      setPreviewingKey(null);
      if (e?.response?.status === 402) {
        toast.error("Preview requires an ElevenLabs key (upgrade voice)");
      } else {
        toast.error(e?.response?.data?.detail || "Preview failed");
      }
    }
  }, [previewingKey, stopPreview]);

  // ── Upload custom bed → refreshes list + auto-selects for target slot ─
  const uploadCustomBed = useCallback(async (file, targetSlot) => {
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) {
      toast.error("File exceeds 60 MB");
      return;
    }
    if (file.type && !/^audio\//.test(file.type) && !/\.(mp3|wav|m4a|aac|flac|ogg)$/i.test(file.name)) {
      toast.error("Unsupported format — use MP3 / WAV / M4A / FLAC / OGG");
      return;
    }
    setUploadingBed(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", "bed");
      form.append("label", file.name.replace(/\.[^.]+$/, ""));
      form.append("source", "user_uploaded");
      form.append("normalize", "false");
      const r = await api.post("/imaging/assets/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const asset = r.data;
      const bedEntry = {
        asset_id:     asset.asset_id,
        label:        asset.label,
        content_type: asset.content_type,
        duration_s:   asset.duration_seconds,
        genre:        asset.genre || "uploaded",
        source:       "user_uploaded",
        created_at:   asset.created_at,
      };
      setBeds((prev) => [bedEntry, ...prev]);
      // Auto-select in the slot that triggered the upload
      if (targetSlot === "intro") setIntroBedId(asset.asset_id);
      else if (targetSlot === "outro") setOutroBedId(asset.asset_id);
      else if (targetSlot === "under") setUnderBedId(asset.asset_id);
      toast.success("Bed uploaded — loudness-normalized at render time");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingBed(false);
      if (bedFileInputRef.current) bedFileInputRef.current.value = "";
    }
  }, []);

  // ── Drafts: fetch latest 5 on mount ────────────────────────────
  const refreshDrafts = useCallback(async () => {
    try {
      const r = await api.get("/podcast/creator/drafts?limit=5");
      setDrafts(r.data.items || []);
    } catch {
      // Non-fatal — panel just stays empty
    } finally {
      setDraftsLoading(false);
    }
  }, []);

  useEffect(() => { refreshDrafts(); }, [refreshDrafts]);

  // ── Draft title derivation ─────────────────────────────────────
  // Keeps the resume-drafts list readable even when the user
  // hasn't gotten past the topic input.
  const derivedDraftTitle = useCallback(() => {
    if (script?.title) return script.title.slice(0, 200);
    if (sourceMode === "topic" && topic.trim()) return topic.trim().slice(0, 90);
    if (sourceMode === "url"   && urlInput.trim()) {
      try {
        const u = new URL(urlInput.trim());
        return `${u.hostname}${u.pathname}`.slice(0, 90);
      } catch { return urlInput.trim().slice(0, 90); }
    }
    if (sourceMode === "text"  && textInput.trim()) return textInput.trim().slice(0, 90);
    return "Untitled draft";
  }, [script, sourceMode, topic, urlInput, textInput]);

  // ── Auto-save (debounced 1.2s) ────────────────────────────────
  // Only kicks in once the user has entered something meaningful
  // (topic/url/text) or gone past step 1. Prevents flooding the DB
  // with empty drafts while the user is still on Format.
  const hasContentToSave = useMemo(() => {
    if (step > 1) return true;
    return !!(topic.trim() || urlInput.trim() || textInput.trim() || script);
  }, [step, topic, urlInput, textInput, script]);

  useEffect(() => {
    if (!hasContentToSave) return;
    if (renderedEp) return; // Don't save drafts of already-rendered episodes
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      if (draftBusy) return;
      setDraftBusy(true);
      try {
        const r = await api.post("/podcast/creator/drafts", {
          draft_id: draftId,
          title:    derivedDraftTitle(),
          format,
          segments: script?.segments || [],
          metadata: {
            step,
            sourceMode,
            topic, urlInput, textInput,
            targetMinutes, tone, hostAName, hostBName,
            voiceAId, voiceBId,
            musicMode, introBedId, outroBedId, underBedId, underBedGain,
            publishToVault,
            scriptTitle:       script?.title || null,
            scriptDescription: script?.description || null,
          },
        });
        if (r.data.draft_id && r.data.draft_id !== draftId) {
          setDraftId(r.data.draft_id);
        }
        setDraftSavedAt(Date.now());
      } catch {
        // Non-fatal — user can still finish their episode
      } finally {
        setDraftBusy(false);
      }
    }, 1200);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [
    hasContentToSave, draftId, format, sourceMode, step,
    topic, urlInput, textInput, targetMinutes, tone, hostAName, hostBName,
    script, voiceAId, voiceBId, musicMode, introBedId, outroBedId, underBedId,
    underBedGain, publishToVault, renderedEp,
  ]);

  // ── Resume a draft (one-click restore) ─────────────────────────
  const resumeDraft = useCallback((d) => {
    stopPreview();
    const md = d.metadata || {};
    setDraftId(d.draft_id);
    setFormat(d.format || "solo");
    setSourceMode(md.sourceMode || "topic");
    setTopic(md.topic || "");
    setUrlInput(md.urlInput || "");
    setTextInput(md.textInput || "");
    setTargetMinutes(md.targetMinutes || 5);
    setTone(md.tone || "informative, engaging");
    setHostAName(md.hostAName || "Host");
    setHostBName(md.hostBName || "Co-host");
    // Script restore — only if segments are non-empty
    if (Array.isArray(d.segments) && d.segments.length > 0) {
      setScript({
        title:       md.scriptTitle || d.title,
        description: md.scriptDescription || "",
        segments:    d.segments,
      });
    } else {
      setScript(null);
    }
    if (md.voiceAId) setVoiceAId(md.voiceAId);
    if (md.voiceBId) setVoiceBId(md.voiceBId);
    if (md.musicMode) setMusicMode(md.musicMode);
    setIntroBedId(md.introBedId || null);
    setOutroBedId(md.outroBedId || null);
    setUnderBedId(md.underBedId || null);
    if (typeof md.underBedGain === "number") setUnderBedGain(md.underBedGain);
    if (typeof md.publishToVault === "boolean") setPublishToVault(md.publishToVault);
    // Jump to the step the user was on (clamp between Source and Music —
    // no point resuming into the final Render step, since audio isn't
    // persisted in drafts).
    const target = Math.max(1, Math.min(4, md.step || (d.segments?.length ? 3 : 2)));
    setStep(target);
    toast.success(`Resumed "${(d.title || 'Untitled').slice(0, 40)}"`);
  }, [stopPreview]);

  // ── Delete a draft ─────────────────────────────────────────────
  const deleteDraft = useCallback(async (targetDraftId, e) => {
    if (e) e.stopPropagation();
    if (!targetDraftId) return;
    if (!await appConfirm("Delete this draft? This cannot be undone.")) return;
    try {
      await api.delete(`/podcast/creator/drafts/${targetDraftId}`);
      setDrafts((prev) => prev.filter((d) => d.draft_id !== targetDraftId));
      if (targetDraftId === draftId) {
        setDraftId(null);
      }
      toast.success("Draft deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  }, [draftId]);


  // ── Script generation ────────────────────────────────────────────
  const generateScript = useCallback(async () => {
    setGenerating(true);
    setScript(null);
    try {
      let endpoint, payload;
      if (sourceMode === "topic") {
        if (!topic.trim() || topic.trim().length < 6) {
          toast.error("Give me a topic (min 6 chars)"); setGenerating(false); return;
        }
        endpoint = "/podcast/creator/script/from-topic";
        payload = { topic: topic.trim(), format, target_minutes: targetMinutes, tone,
                    host_a_name: hostAName, host_b_name: hostBName };
      } else if (sourceMode === "url") {
        if (!urlInput.trim().startsWith("http")) {
          toast.error("URL must start with http:// or https://"); setGenerating(false); return;
        }
        endpoint = "/podcast/creator/script/from-url";
        payload = { url: urlInput.trim(), format, target_minutes: targetMinutes, tone,
                    host_a_name: hostAName, host_b_name: hostBName };
      } else {
        if (textInput.trim().length < 20) {
          toast.error("Script too short (min 20 chars)"); setGenerating(false); return;
        }
        endpoint = "/podcast/creator/script/from-text";
        payload = { text: textInput.trim(), format,
                    host_a_name: hostAName, host_b_name: hostBName };
      }
      const r = await api.post(endpoint, payload);
      setScript(r.data);
      const modeMsg = r.data.generation_mode === "chunked"
        ? ` (${r.data.sections_count}-section long-form)` : "";
      toast.success(`Script generated — ${r.data.segments.length} segments${modeMsg}`);
    } catch (e) {
      // Cloudflare / K8s ingress edge timeouts return HTML bodies that
      // look like garbage when shown as-is. Detect those and show a
      // friendly message. The backend has its own 85s asyncio timeout,
      // so a proper 504 with `detail` would flow through the first
      // branch; this catches the outer proxy layer only.
      const status = e?.response?.status;
      const raw = e?.response?.data;
      const detail = typeof raw === "object" ? raw?.detail : null;
      let msg;
      if (detail) {
        msg = detail;
      } else if (typeof raw === "string" && (
        raw.startsWith("<") || raw.toLowerCase().includes("cloudflare"))) {
        msg = "Server took too long — try a shorter target length or retry (this usually works on retry)";
      } else if (status === 502 || status === 504) {
        msg = "Server timed out — try a shorter target length or retry";
      } else {
        msg = "Script generation failed";
      }
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  }, [sourceMode, topic, urlInput, textInput, format, targetMinutes, tone, hostAName, hostBName]);

  // ── Render ───────────────────────────────────────────────────────
  const renderEpisode = useCallback(async () => {
    if (!script || !script.segments?.length) {
      toast.error("Generate a script first");
      return;
    }
    setRendering(true);
    setRenderedEp(null);
    setAudioUrl(null);
    setRenderJob({ progress: 0, stage: "Starting…" });
    try {
      const start = await api.post("/podcast/creator/render", {
        title:              script.title || "Untitled episode",
        format,
        description:        script.description || "",
        segments:           script.segments,
        voice_a_id:         voiceAId,
        voice_b_id:         format === "duet" ? voiceBId : null,
        music_mode:         musicMode,
        intro_bed_asset_id: musicMode !== "dry" ? introBedId : null,
        outro_bed_asset_id: musicMode !== "dry" ? outroBedId : null,
        under_bed_asset_id: musicMode === "full" ? underBedId : null,
        under_bed_gain:     underBedGain,
        publish_to_vault:   publishToVault,
      });
      const jobId = start.data.job_id;

      // Poll the background render job — long episodes take minutes,
      // far beyond the proxy request window, so the work happens
      // server-side and we just watch progress here.
      const t0 = Date.now();
      let job = null;
      let pollFailures = 0;
      for (;;) {
        await new Promise((res) => setTimeout(res, 2500));
        try {
          const jr = await api.get(`/podcast/creator/render-jobs/${jobId}`);
          job = jr.data;
          pollFailures = 0;
          setRenderJob({ progress: job.progress || 0, stage: job.stage || "Rendering…" });
          if (job.status === "done" || job.status === "failed") break;
        } catch {
          pollFailures += 1;
          if (pollFailures >= 6) throw new Error("Lost contact with the render job — please retry");
        }
        if (Date.now() - t0 > 20 * 60 * 1000) throw new Error("Render timed out after 20 minutes — please retry");
      }
      if (job.status === "failed") {
        toast.error(job.error || "Render failed", { duration: 12000 });
        return;
      }

      const ep = job.episode;
      setRenderedEp(ep);
      // Fetch full show notes (the job payload truncates to 1500
      // chars for latency) and the audio blob in parallel.
      const [notesResp, audioBlob] = await Promise.all([
        api.get(`/podcast/creator/episodes/${ep.episode_id}/show-notes.md`),
        api.get(`/podcast/creator/episodes/${ep.episode_id}/audio`, { responseType: "blob" }),
      ]);
      setShowNotes(notesResp.data || ep.show_notes_md || "");
      setAudioUrl(URL.createObjectURL(audioBlob.data));
      setStep(5);
      // Episode is now rendered → this draft is no longer "in progress".
      // Best-effort delete + refresh the panel so it doesn't sit forever.
      if (draftId) {
        try {
          await api.delete(`/podcast/creator/drafts/${draftId}`);
          setDraftId(null);
        } catch { /* non-fatal */ }
      }
      refreshDrafts();
      toast.success("Episode rendered");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Render failed", { duration: 12000 });
    } finally {
      setRendering(false);
      setRenderJob(null);
    }
  }, [script, format, voiceAId, voiceBId, musicMode, introBedId, outroBedId, underBedId,
      underBedGain, publishToVault, draftId, refreshDrafts]);

  const downloadBundle = useCallback(async () => {
    if (!renderedEp) return;
    try {
      const r = await api.get(`/podcast/creator/episodes/${renderedEp.episode_id}/bundle`,
                              { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const safe = renderedEp.title.replace(/[^A-Za-z0-9_\- ]+/g, "_");
      a.href = url;
      a.download = `${safe}_episode.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded MP3 + show notes + manifest");
    } catch {
      toast.error("Bundle download failed");
    }
  }, [renderedEp]);

  const startOver = useCallback(() => {
    stopPreview();
    setStep(1);
    setScript(null);
    setRenderedEp(null);
    setAudioUrl(null);
    setShowNotes("");
    setTopic("");
    setUrlInput("");
    setTextInput("");
    // A fresh session gets a fresh draft row — otherwise autosave would
    // keep overwriting whatever draft we resumed from.
    setDraftId(null);
    setDraftSavedAt(null);
    // Refresh the drafts panel so the user sees any drafts that were
    // saved during the session they just finished.
    refreshDrafts();
  }, [stopPreview, refreshDrafts]);

  const wizardSteps = useMemo(() => [
    { n: 1, label: "Format" },
    { n: 2, label: "Source" },
    { n: 3, label: "Voices" },
    { n: 4, label: "Music" },
    { n: 5, label: "Render" },
  ], []);

  const canGoNext = useMemo(() => {
    if (step === 1) return true;
    if (step === 2) return !!script;
    if (step === 3) return !!voiceAId && (format === "solo" || !!voiceBId);
    if (step === 4) return true;
    return false;
  }, [step, script, voiceAId, voiceBId, format]);

  return (
    <div className="min-h-full" style={{ background: T.bg, color: T.text }}>
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-8 md:py-12" data-testid="podcast-creator">
        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <div className="rounded-lg p-2" style={{ background: `${T.primary}22`, border: `1px solid ${T.primary}55` }}>
            <Podcast size={18} style={{ color: T.primary }} />
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: -0.5 }}>
            Podcast Creator
          </h1>
          <span className="rounded px-1.5 py-0.5 ml-1"
                style={{ background: T.primary, color: "#000", fontSize: 10, fontWeight: 700 }}>
            NEW
          </span>
          {/* Auto-save chip — only shown once we've persisted at least once
              and the user isn't looking at a rendered episode. Keeps the
              user reassured without being visually noisy. */}
          {draftSavedAt && !renderedEp && (
            <span className="rounded-full inline-flex items-center gap-1 px-2 py-0.5 ml-auto"
                  data-testid="podcast-draft-saved-chip"
                  style={{ background: `${T.success}1a`, border: `1px solid ${T.success}55`,
                           color: T.success, fontSize: 10, fontWeight: 500 }}>
              {draftBusy
                ? <Loader2 size={10} className="animate-spin" />
                : <Check size={10} />}
              {draftBusy ? "Saving…" : `Draft saved · ${formatAgo(new Date(draftSavedAt).toISOString())}`}
            </span>
          )}
        </div>
        <p className="mb-8 max-w-2xl" style={{ color: T.muted, fontSize: 14, lineHeight: 1.5 }}>
          Turn a topic, an article, or your own script into a fully-narrated, music-bedded podcast episode with
          auto-generated show notes and chapters. Solo host or two-speaker conversation — you choose per episode.
        </p>

        {/* Step tracker */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {wizardSteps.map((s, i) => {
            const done = step > s.n;
            const active = step === s.n;
            return (
              <React.Fragment key={s.n}>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center rounded-full"
                        style={{
                          width: 22, height: 22,
                          background: done ? T.success : active ? T.purple : T.elevated,
                          color: done || active ? "#000" : T.muted,
                          fontSize: 11, fontWeight: 700,
                          border: `1px solid ${done ? T.success : active ? T.purple : T.border}`,
                        }}>
                    {done ? <Check size={11} /> : s.n}
                  </span>
                  <span style={{ fontSize: 12, color: active ? T.text : T.muted, fontWeight: active ? 600 : 500 }}>
                    {s.label}
                  </span>
                </div>
                {i < wizardSteps.length - 1 && <ChevronRight size={12} style={{ color: T.faint }} />}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── STEP 1: Format ────────────────────────────────────── */}
        {step === 1 && (
          <>
            {/* Resume drafts panel — surfaces the user's latest 5
                in-progress episodes so they can pick up where they
                left off. Hidden on the fresh-empty state and while
                the initial list is still loading. */}
            {!draftsLoading && drafts.length > 0 && (
              <div className="rounded-xl p-4 mb-4"
                   style={{ background: T.card, border: `1px solid ${T.border}` }}
                   data-testid="podcast-drafts-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="rounded-lg p-1.5"
                         style={{ background: `${T.warning}22`, border: `1px solid ${T.warning}55` }}>
                      <FileText size={13} style={{ color: T.warning }} />
                    </div>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
                      Resume a draft
                    </h3>
                    <span style={{ fontSize: 11, color: T.muted }}>
                      · {drafts.length} in progress
                    </span>
                  </div>
                  <button
                    type="button"
                    data-testid="podcast-drafts-refresh"
                    onClick={refreshDrafts}
                    className="rounded-full inline-flex items-center gap-1 px-2 py-1"
                    style={{ background: T.elevated, border: `1px solid ${T.border}`,
                             color: T.muted, fontSize: 10 }}
                    title="Refresh"
                  >
                    <RefreshCw size={10} />
                  </button>
                </div>

                <div className="space-y-1.5">
                  {drafts.map((d) => (
                    <DraftRow
                      key={d.draft_id}
                      draft={d}
                      onResume={() => resumeDraft(d)}
                      onDelete={(e) => deleteDraft(d.draft_id, e)}
                    />
                  ))}
                </div>
              </div>
            )}

            <StepCard title="Pick a format" subtitle="You can change this per episode later.">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {FORMATS.map((f) => {
                  const active = format === f.id;
                  const Icon = f.icon;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      data-testid={`podcast-format-${f.id}`}
                      onClick={() => setFormat(f.id)}
                      className="text-left rounded-xl p-4 transition"
                      style={{
                        background: active ? `linear-gradient(180deg, ${T.purple}1a, ${T.card} 60%)` : T.elevated,
                        border: `1px solid ${active ? T.purple : T.border}`,
                      }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon size={16} style={{ color: active ? T.purple : T.muted }} />
                        <span style={{ fontSize: 14, fontWeight: 600 }}>{f.title}</span>
                        {active && <Check size={14} style={{ color: T.purple, marginLeft: "auto" }} />}
                      </div>
                      <div style={{ color: T.muted, fontSize: 12 }}>{f.subtitle}</div>
                    </button>
                  );
                })}
              </div>
              <StepFooter
                onBack={null}
                onNext={() => setStep(2)}
                nextLabel="Next: Source →"
                testid="podcast-step1-next"
              />
            </StepCard>
          </>
        )}

        {/* ── STEP 2: Source + script generation ──────────────────── */}
        {step === 2 && (
          <StepCard title="Source" subtitle="Where's the content coming from?">
            {/* Source mode tabs */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {SOURCE_MODES.map((s) => {
                const active = sourceMode === s.id;
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`podcast-source-${s.id}`}
                    onClick={() => { setSourceMode(s.id); setScript(null); }}
                    className="rounded-full px-3 py-1.5 inline-flex items-center gap-1.5"
                    style={{
                      background: active ? `${T.purple}22` : T.elevated,
                      border: `1px solid ${active ? T.purple : T.border}`,
                      color: active ? T.purple : T.muted,
                      fontSize: 12,
                    }}
                  >
                    <Icon size={12} />
                    {s.label}
                  </button>
                );
              })}
            </div>
            <div className="mb-4" style={{ fontSize: 11, color: T.faint }}>
              {SOURCE_MODES.find((s) => s.id === sourceMode)?.hint}
            </div>

            {/* Source input */}
            {sourceMode === "topic" && (
              <textarea
                data-testid="podcast-topic-input"
                placeholder="e.g., The history of soul music from the 60s to today"
                value={topic}
                onChange={(e) => setTopic(e.target.value.slice(0, 1000))}
                rows={2}
                className="w-full rounded-lg px-3 py-2 mb-3"
                style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 13, resize: "vertical" }}
              />
            )}
            {sourceMode === "url" && (
              <input
                type="url"
                data-testid="podcast-url-input"
                placeholder="https://example.com/article-to-summarize"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                className="w-full rounded-lg px-3 py-2 mb-3"
                style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 13 }}
              />
            )}
            {sourceMode === "text" && (
              <textarea
                data-testid="podcast-text-input"
                placeholder={format === "duet"
                  ? "Alex: Welcome to the show.\n\nJordan: Thanks for having me."
                  : "Paste your full script here..."}
                value={textInput}
                onChange={(e) => setTextInput(e.target.value.slice(0, 100000))}
                rows={10}
                className="w-full rounded-lg px-3 py-2 mb-3 font-mono"
                style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 12, resize: "vertical" }}
              />
            )}

            {/* Params — hidden in text mode (script is already there) */}
            {sourceMode !== "text" && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <TextInput label="Target minutes" value={targetMinutes} onChange={(v) => setTargetMinutes(Number(v) || 5)} type="number" min={1} max={30} testid="podcast-target-minutes" />
                <TextInput label="Tone" value={tone} onChange={setTone} testid="podcast-tone" />
                <TextInput label="Host A name" value={hostAName} onChange={setHostAName} testid="podcast-host-a-name" />
                {format === "duet" && (
                  <TextInput label="Host B name" value={hostBName} onChange={setHostBName} testid="podcast-host-b-name" />
                )}
              </div>
            )}

            <div className="flex gap-2 mb-4">
              <button
                type="button"
                data-testid="podcast-generate-script"
                onClick={generateScript}
                disabled={generating}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2"
                style={{
                  background: `linear-gradient(135deg,${T.primary} 0%,${T.purple} 100%)`,
                  color: "#fff", fontSize: 13, fontWeight: 500,
                  opacity: generating ? 0.6 : 1,
                }}
              >
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {generating ? "Writing script…" : (script ? "Regenerate script" : "Generate script")}
              </button>
            </div>

            {/* Script preview */}
            {script && (
              <div className="rounded-lg p-3 mb-4" style={{ background: T.elevated, border: `1px solid ${T.border}` }}>
                <div className="mb-2">
                  <FieldLabel>Title</FieldLabel>
                  <input
                    type="text"
                    data-testid="podcast-script-title"
                    value={script.title}
                    onChange={(e) => setScript({ ...script, title: e.target.value })}
                    className="w-full rounded px-2 py-1.5"
                    style={{ background: T.card, border: `1px solid ${T.border}`, color: T.text, fontSize: 13, fontWeight: 500 }}
                  />
                </div>
                <FieldLabel>Description</FieldLabel>
                <textarea
                  data-testid="podcast-script-description"
                  value={script.description || ""}
                  onChange={(e) => setScript({ ...script, description: e.target.value })}
                  rows={2}
                  className="w-full rounded px-2 py-1.5 mb-2"
                  style={{ background: T.card, border: `1px solid ${T.border}`, color: T.text, fontSize: 12 }}
                />
                <FieldLabel>{script.segments.length} segments · click a segment to edit</FieldLabel>
                <div className="rounded max-h-64 overflow-y-auto"
                     style={{ background: T.card, border: `1px solid ${T.border}` }}>
                  {script.segments.map((s, i) => (
                    <div key={i} className="px-2 py-1.5 flex items-start gap-2 border-b"
                         style={{ borderColor: T.border }}
                         data-testid={`podcast-segment-${i}`}>
                      <span className="rounded px-1.5 py-0.5"
                            style={{
                              background: s.speaker === "host_b" ? T.purple : T.primary,
                              color: "#000", fontSize: 9, fontWeight: 700,
                              minWidth: 44, textAlign: "center", flexShrink: 0,
                            }}>
                        {s.speaker === "host_b" ? "HOST B" : "HOST A"}
                      </span>
                      <textarea
                        value={s.text}
                        onChange={(e) => {
                          const next = [...script.segments];
                          next[i] = { ...s, text: e.target.value };
                          setScript({ ...script, segments: next });
                        }}
                        rows={2}
                        className="flex-1 bg-transparent text-neutral-300 outline-none resize-none"
                        style={{ fontSize: 12, lineHeight: 1.5 }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <StepFooter
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
              nextLabel="Next: Voices →"
              nextDisabled={!canGoNext}
              testid="podcast-step2-next"
            />
          </StepCard>
        )}

        {/* ── STEP 3: Voices ─────────────────────────────────────── */}
        {step === 3 && (
          <StepCard title="Choose voices" subtitle={format === "duet" ? "Pick two voices — one per host. Click the play icon to preview." : "Pick a narrator voice. Click the play icon to preview."}>
            <VoicePicker
              label={format === "duet" ? "Host A" : "Narrator"}
              voices={voices}
              value={voiceAId}
              onChange={setVoiceAId}
              testidPrefix="podcast-voice-a"
              previewingKey={previewingKey}
              onPreview={playPreview}
            />
            {format === "duet" && (
              <VoicePicker
                label="Host B"
                voices={voices}
                value={voiceBId}
                onChange={setVoiceBId}
                testidPrefix="podcast-voice-b"
                excludeId={voiceAId}
                previewingKey={previewingKey}
                onPreview={playPreview}
              />
            )}
            <StepFooter
              onBack={() => setStep(2)}
              onNext={() => setStep(4)}
              nextLabel="Next: Music →"
              nextDisabled={!canGoNext}
              testid="podcast-step3-next"
            />
          </StepCard>
        )}

        {/* ── STEP 4: Music ──────────────────────────────────────── */}
        {step === 4 && (
          <StepCard title="Music" subtitle="Pick a music treatment for this episode.">
            <input
              ref={bedFileInputRef}
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.aac,.flac,.ogg"
              onChange={(e) => uploadCustomBed(e.target.files?.[0], bedFileInputRef.current?.dataset?.slot || "intro")}
              className="hidden"
              data-testid="podcast-bed-file-input"
            />
            <div className="space-y-2 mb-4">
              {MUSIC_MODES.map((m) => {
                const active = musicMode === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`podcast-music-${m.id}`}
                    onClick={() => setMusicMode(m.id)}
                    className="w-full text-left rounded-lg px-3 py-3 flex items-start gap-3"
                    style={{
                      background: active ? `${T.purple}1a` : T.elevated,
                      border: `1px solid ${active ? T.purple : T.border}`,
                    }}
                  >
                    <div className="rounded-full mt-1"
                         style={{
                           width: 12, height: 12,
                           background: active ? T.purple : "transparent",
                           border: `2px solid ${active ? T.purple : T.faint}`,
                           flexShrink: 0,
                         }} />
                    <div className="flex-1">
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{m.title}</div>
                      <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{m.subtitle}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {musicMode !== "dry" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Beds — pick from library or upload your own
                  </span>
                  <button
                    type="button"
                    data-testid="podcast-upload-bed"
                    onClick={() => {
                      if (bedFileInputRef.current) bedFileInputRef.current.dataset.slot = "intro";
                      bedFileInputRef.current?.click();
                    }}
                    disabled={uploadingBed}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1"
                    style={{
                      background: T.elevated,
                      border: `1px solid ${T.primary}`,
                      color: T.primary, fontSize: 11, fontWeight: 500,
                      opacity: uploadingBed ? 0.6 : 1,
                    }}
                  >
                    {uploadingBed ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                    {uploadingBed ? "Uploading…" : "Upload custom bed"}
                  </button>
                </div>
                <BedPicker label="Intro sting" beds={beds} value={introBedId} onChange={setIntroBedId} testid="podcast-intro-bed" previewingKey={previewingKey} onPreview={playPreview} />
                <BedPicker label="Outro sting" beds={beds} value={outroBedId} onChange={setOutroBedId} testid="podcast-outro-bed" previewingKey={previewingKey} onPreview={playPreview} />
                {musicMode === "full" && (
                  <>
                    <BedPicker label="Under-narration bed" beds={beds} value={underBedId} onChange={setUnderBedId} testid="podcast-under-bed" previewingKey={previewingKey} onPreview={playPreview} />
                    <div className="rounded-lg p-3" style={{ background: T.elevated, border: `1px solid ${T.border}` }}>
                      <FieldLabel>Under-bed gain (lower = quieter)</FieldLabel>
                      <input
                        type="range"
                        data-testid="podcast-under-gain"
                        value={underBedGain} min={0} max={0.5} step={0.02}
                        onChange={(e) => setUnderBedGain(Number(e.target.value))}
                        className="w-full"
                        style={{ accentColor: T.purple }}
                      />
                      <span style={{ fontSize: 10, color: T.muted }}>{Math.round(underBedGain * 100)}% — recommended 15-25%</span>
                    </div>
                  </>
                )}
              </div>
            )}

            <StepFooter
              onBack={() => setStep(3)}
              onNext={() => setStep(5)}
              nextLabel="Next: Render →"
              testid="podcast-step4-next"
            />
          </StepCard>
        )}

        {/* ── STEP 5: Render & result ────────────────────────────── */}
        {step === 5 && (
          <StepCard title="Render episode" subtitle="Review, then bake the final MP3.">
            {!renderedEp && script && (
              <>
                {/* Summary */}
                <div className="rounded-lg p-3 mb-4" style={{ background: T.elevated, border: `1px solid ${T.border}` }}>
                  <SummaryRow label="Title"   value={script.title} />
                  <SummaryRow label="Format"  value={format === "duet" ? "Two-speaker conversation" : "Solo host"} />
                  <SummaryRow label="Segments" value={`${script.segments.length} segments`} />
                  <SummaryRow label="Voice A" value={(voices.find((v) => v.id === voiceAId) || {}).label || voiceAId} />
                  {format === "duet" && <SummaryRow label="Voice B" value={(voices.find((v) => v.id === voiceBId) || {}).label || voiceBId} />}
                  <SummaryRow label="Music"   value={MUSIC_MODES.find((m) => m.id === musicMode)?.title} />
                </div>

                <label className="flex items-center gap-2 mb-4 cursor-pointer">
                  <input type="checkbox" checked={publishToVault}
                         data-testid="podcast-publish-to-vault"
                         onChange={(e) => setPublishToVault(e.target.checked)}
                         className="accent-violet-500" />
                  <span style={{ fontSize: 12 }}>
                    Publish to Podcast Vault (appears in your iTunes/Spotify RSS feed immediately)
                  </span>
                </label>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid="podcast-render"
                    onClick={renderEpisode}
                    disabled={rendering}
                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-2"
                    style={{
                      background: `linear-gradient(135deg,${T.primary} 0%,${T.purple} 100%)`,
                      color: "#fff", fontSize: 13, fontWeight: 600,
                      opacity: rendering ? 0.6 : 1,
                    }}
                  >
                    {rendering ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                    {rendering
                      ? (renderJob?.stage
                          ? `${renderJob.stage} — ${renderJob.progress || 0}%`
                          : `Rendering (${script.segments.length} segments)…`)
                      : "Render episode"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep(4)}
                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-2"
                    style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.muted, fontSize: 12 }}
                  >
                    <ChevronLeft size={12} />
                    Back
                  </button>
                </div>
                {rendering && renderJob && (
                  <div className="mt-3" data-testid="render-progress">
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: T.elevated }}>
                      <div className="h-full rounded-full transition-all duration-500"
                           style={{ width: `${renderJob.progress || 0}%`,
                                    background: `linear-gradient(90deg,${T.primary},${T.purple})` }} />
                    </div>
                    <div className="mt-1.5" style={{ fontSize: 11, color: T.muted }}>
                      {renderJob.stage} — you can keep this tab open, rendering happens on the server.
                    </div>
                  </div>
                )}
              </>
            )}

            {renderedEp && (
              <div className="space-y-4">
                <div className="rounded-lg p-4"
                     style={{ background: `${T.success}0a`, border: `1px solid ${T.success}55` }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Check size={16} style={{ color: T.success }} />
                    <span style={{ fontSize: 14, fontWeight: 600 }}>
                      Rendered · {renderedEp.duration_seconds}s · {(renderedEp.audio_size_bytes / 1024).toFixed(0)} KB
                    </span>
                    {renderedEp.published && (
                      <span className="rounded-full px-2 py-0.5 ml-2"
                            style={{ background: T.primary, color: "#000", fontSize: 10, fontWeight: 600 }}>
                        Published to Vault
                      </span>
                    )}
                  </div>
                  {audioUrl && (
                    <audio controls src={audioUrl} className="w-full mb-3" data-testid="podcast-audio-player" />
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      data-testid="podcast-download-bundle"
                      onClick={downloadBundle}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                      style={{
                        background: `linear-gradient(135deg,${T.primary} 0%,${T.purple} 100%)`,
                        color: "#fff", fontSize: 12, fontWeight: 500,
                      }}
                    >
                      <Download size={12} />
                      Download MP3 + show notes (.zip)
                    </button>
                    <button
                      type="button"
                      data-testid="podcast-open-vault"
                      onClick={() => navigate("/dashboard/podcasts")}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5"
                      style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.muted, fontSize: 12 }}
                    >
                      <ExternalLink size={12} />
                      Open in Podcast Vault
                    </button>
                    <button
                      type="button"
                      data-testid="podcast-start-over"
                      onClick={startOver}
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 ml-auto"
                      style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.muted, fontSize: 12 }}
                    >
                      <Sparkles size={12} />
                      Create another
                    </button>
                  </div>
                </div>

                {/* Chapters */}
                {renderedEp.chapters && renderedEp.chapters.length > 0 && (
                  <div className="rounded-lg p-3" style={{ background: T.elevated, border: `1px solid ${T.border}` }}>
                    <FieldLabel>Chapters</FieldLabel>
                    <div className="space-y-1">
                      {renderedEp.chapters.map((ch, i) => {
                        const mm = Math.floor(ch.start_seconds / 60);
                        const ss = Math.floor(ch.start_seconds) % 60;
                        return (
                          <div key={i} className="flex items-center gap-2" data-testid={`podcast-chapter-${i}`}>
                            <span style={{ fontVariantNumeric: "tabular-nums", color: T.muted, fontSize: 11, minWidth: 40 }}>
                              {String(mm).padStart(2, "0")}:{String(ss).padStart(2, "0")}
                            </span>
                            <span style={{ fontSize: 12 }}>{ch.title}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Show notes preview */}
                {showNotes && (
                  <div className="rounded-lg p-3" style={{ background: T.elevated, border: `1px solid ${T.border}` }}>
                    <FieldLabel>Show notes preview</FieldLabel>
                    <pre className="text-neutral-300 whitespace-pre-wrap max-h-72 overflow-y-auto"
                         data-testid="podcast-show-notes"
                         style={{ fontSize: 11, lineHeight: 1.5, fontFamily: "ui-monospace, monospace" }}>
                      {showNotes.slice(0, 2000)}{showNotes.length > 2000 ? "\n\n…(more in the downloaded bundle)" : ""}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </StepCard>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────
function StepCard({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl p-6" style={{ background: T.card, border: `1px solid ${T.border}` }}>
      <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 4 }}>{title}</h2>
      {subtitle && <p style={{ color: T.muted, fontSize: 12, marginBottom: 20 }}>{subtitle}</p>}
      {children}
    </div>
  );
}

function StepFooter({ onBack, onNext, nextLabel, nextDisabled, testid }) {
  return (
    <div className="flex items-center justify-between mt-6">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2"
          style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.muted, fontSize: 12 }}
        >
          <ChevronLeft size={12} />
          Back
        </button>
      ) : <div />}
      <button
        type="button"
        data-testid={testid}
        onClick={onNext}
        disabled={nextDisabled}
        className="inline-flex items-center gap-1.5 rounded-full px-4 py-2"
        style={{
          background: `linear-gradient(135deg,${T.primary} 0%,${T.purple} 100%)`,
          color: "#fff", fontSize: 12, fontWeight: 500,
          opacity: nextDisabled ? 0.5 : 1,
        }}
      >
        {nextLabel}
      </button>
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <div style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
      {children}
    </div>
  );
}

function TextInput({ label, value, onChange, type = "text", min, max, testid }) {
  return (
    <label className="block">
      <FieldLabel>{label}</FieldLabel>
      <input
        type={type}
        data-testid={testid}
        value={value}
        min={min} max={max}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded px-2 py-1.5"
        style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 12 }}
      />
    </label>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-3 py-1">
      <span style={{ fontSize: 10, color: T.muted, textTransform: "uppercase", letterSpacing: 0.5, minWidth: 80 }}>{label}</span>
      <span style={{ fontSize: 12, color: T.text }} className="flex-1">{value}</span>
    </div>
  );
}

function VoicePicker({ label, voices, value, onChange, testidPrefix, excludeId, previewingKey, onPreview }) {
  const available = voices.filter((v) => v.id !== excludeId);
  return (
    <div className="mb-4">
      <FieldLabel>{label}</FieldLabel>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {available.map((v) => {
          const active = value === v.id;
          const key = `${testidPrefix}:${v.id}`;
          const isPlaying = previewingKey === key;
          return (
            <div
              key={v.id}
              className="flex items-center gap-2 rounded-lg px-3 py-2 relative"
              style={{
                background: active ? `${T.purple}1a` : T.elevated,
                border: `1px solid ${active ? T.purple : T.border}`,
                opacity: v.needs_key ? 0.55 : 1,
              }}
            >
              <button
                type="button"
                data-testid={`${testidPrefix}-preview-${v.id}`}
                disabled={v.needs_key}
                onClick={(e) => {
                  e.stopPropagation();
                  if (v.needs_key || !onPreview) return;
                  onPreview(key, `/podcast/creator/voice-preview/${v.id}`);
                }}
                className="rounded-full inline-flex items-center justify-center flex-shrink-0"
                title={isPlaying ? "Stop preview" : "Preview voice"}
                style={{
                  width: 24, height: 24,
                  background: isPlaying ? T.warning : (active ? T.purple : T.faint),
                  color: isPlaying || active ? "#000" : T.text,
                  border: `1px solid ${isPlaying ? T.warning : (active ? T.purple : T.border)}`,
                  cursor: v.needs_key ? "not-allowed" : "pointer",
                }}
              >
                {isPlaying ? <Pause size={10} /> : <Play size={10} style={{ marginLeft: 1 }} />}
              </button>
              <button
                type="button"
                data-testid={`${testidPrefix}-${v.id}`}
                onClick={() => !v.needs_key && onChange(v.id)}
                disabled={v.needs_key}
                className="flex-1 min-w-0 text-left"
                style={{ cursor: v.needs_key ? "not-allowed" : "pointer" }}
              >
                <div style={{ fontSize: 11, fontWeight: 500, color: T.text }} className="truncate">
                  {v.label.split(" — ")[0]}
                </div>
                <div style={{ fontSize: 9, color: T.muted }} className="truncate">
                  {v.label.split(" — ")[1] || v.engine}
                </div>
              </button>
              {v.long_form_recommended && !v.needs_key && (
                <Star size={10} style={{ color: T.warning, position: "absolute", top: 4, right: 4 }} />
              )}
              {v.premium && (
                <span className="rounded px-1"
                      style={{ background: T.warning, color: "#000", fontSize: 8, fontWeight: 700, position: "absolute", top: 4, right: 4 }}>
                  UP
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-1" style={{ fontSize: 10, color: T.faint }}>
        <Star size={9} className="inline mr-1" style={{ color: T.warning }} />
        Voices marked with a star hold up well over long-form narration. Click the play icon to preview.
      </div>
    </div>
  );
}

function BedPicker({ label, beds, value, onChange, testid, previewingKey, onPreview }) {
  const selected = beds.find((b) => b.asset_id === value);
  const key = value ? `bed:${value}` : null;
  const isPlaying = key && previewingKey === key;
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="flex items-center gap-2">
        <select
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          className="flex-1 rounded px-2 py-1.5"
          style={{ background: T.elevated, border: `1px solid ${T.border}`, color: T.text, fontSize: 12 }}
        >
          <option value="">— none —</option>
          {beds.map((b) => (
            <option key={b.asset_id} value={b.asset_id}>
              {b.label || b.asset_id}{b.duration_s ? ` (${Math.round(b.duration_s)}s)` : ""}
              {b.source === "user_uploaded" ? " · yours" : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          data-testid={`${testid}-preview`}
          disabled={!value}
          onClick={() => value && onPreview(`bed:${value}`, `/imaging/assets/${value}/audio`)}
          title={isPlaying ? "Stop preview" : (value ? "Preview bed" : "Select a bed first")}
          className="rounded-full inline-flex items-center justify-center flex-shrink-0"
          style={{
            width: 32, height: 32,
            background: isPlaying ? T.warning : (value ? T.purple : T.faint),
            color: isPlaying || value ? "#000" : T.text,
            border: `1px solid ${isPlaying ? T.warning : (value ? T.purple : T.border)}`,
            cursor: value ? "pointer" : "not-allowed",
            opacity: value ? 1 : 0.5,
          }}
        >
          {isPlaying ? <Pause size={12} /> : <Play size={12} style={{ marginLeft: 2 }} />}
        </button>
      </div>
      {selected?.source === "user_uploaded" && (
        <div style={{ fontSize: 10, color: T.primary, marginTop: 4 }}>
          Your upload · loudness-normalized at render time
        </div>
      )}
    </div>
  );
}

// ── DraftRow: one row in the "Resume a draft" panel ──────────────────
function DraftRow({ draft, onResume, onDelete }) {
  const md = draft.metadata || {};
  const stepLabel = ({ 1: "Format", 2: "Source", 3: "Voices", 4: "Music" })[md.step] || "Draft";
  const segCount  = Array.isArray(draft.segments) ? draft.segments.length : 0;
  const ago       = formatAgo(draft.updated_at);
  return (
    <div
      className="w-full rounded-lg px-3 py-2 flex items-center gap-3 transition group cursor-pointer"
      style={{
        background: T.elevated,
        border: `1px solid ${T.border}`,
      }}
      onClick={onResume}
      data-testid={`podcast-draft-row-${draft.draft_id}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onResume(); }}}
    >
      <div className="rounded"
           style={{ background: `${T.warning}22`, padding: 6, flexShrink: 0 }}>
        <FileText size={12} style={{ color: T.warning }} />
      </div>
      <div className="flex-1 min-w-0">
        <div style={{ fontSize: 13, fontWeight: 500, color: T.text }} className="truncate">
          {draft.title || "Untitled draft"}
        </div>
        <div className="flex items-center gap-2" style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>
          <span>{stepLabel}</span>
          <span style={{ color: T.faint }}>·</span>
          <span>{draft.format === "duet" ? "Duet" : "Solo"}</span>
          {segCount > 0 && (
            <>
              <span style={{ color: T.faint }}>·</span>
              <span>{segCount} segment{segCount === 1 ? "" : "s"}</span>
            </>
          )}
          <span style={{ color: T.faint }}>·</span>
          <span className="inline-flex items-center gap-1">
            <Clock size={9} /> {ago}
          </span>
        </div>
      </div>
      <button
        type="button"
        data-testid={`podcast-draft-resume-${draft.draft_id}`}
        onClick={(e) => { e.stopPropagation(); onResume(); }}
        className="rounded-full inline-flex items-center gap-1 px-2.5 py-1 flex-shrink-0"
        style={{ background: T.purple, color: "#fff", fontSize: 11, fontWeight: 600 }}
      >
        Resume <ChevronRight size={11} />
      </button>
      <button
        type="button"
        data-testid={`podcast-draft-delete-${draft.draft_id}`}
        onClick={onDelete}
        className="rounded-full inline-flex items-center justify-center flex-shrink-0 opacity-40 group-hover:opacity-100 transition-opacity"
        title="Delete draft"
        style={{
          width: 26, height: 26,
          background: T.elevated, border: `1px solid ${T.border}`, color: T.danger,
        }}
      >
        <Trash2 size={11} />
      </button>
    </div>
  );
}

// Human-friendly relative timestamp ("2m ago", "3h ago", etc.).
// Small enough to inline rather than pulling in day.js.
function formatAgo(iso) {
  if (!iso) return "just now";
  try {
    const then = new Date(iso).getTime();
    const secs = Math.max(1, Math.round((Date.now() - then) / 1000));
    if (secs < 60)        return `${secs}s ago`;
    if (secs < 3600)      return `${Math.round(secs / 60)}m ago`;
    if (secs < 86400)     return `${Math.round(secs / 3600)}h ago`;
    if (secs < 604800)    return `${Math.round(secs / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return "recently";
  }
}

